/**
 * AP2 (Agent Payments Protocol v0.2) checkout mandates, for agents and merchants.
 *
 * Pryme acts as AP2's "User Credential Issuer": after its online mandate check allows a
 * purchase, it issues a short-lived *open* checkout mandate (an SD-JWT, ES256) pinned to the
 * merchant's signed checkout and bound (`cnf`) to a P-256 key the agent chose. The agent
 * closes it with a KB-SD-JWT hop (`aud`/`nonce` from the merchant) carrying the checkout. The
 * merchant verifies an ordinary AP2 dSD-JWT chain; no Pryme-specific call is needed.
 *
 * Interop is tested against Google's AP2 reference SDK (tests/ap2/reference.py). Mapping and
 * gaps: docs/INTEROP.md. Uses WebCrypto only (Node >= 20, Workers, browsers).
 */
import { b64url, sha256b64, utf8 } from './crypto.js';
export const AP2_VCT = {
    openCheckout: 'mandate.checkout.open.1',
    checkout: 'mandate.checkout.1',
    openPayment: 'mandate.payment.open.1',
    payment: 'mandate.payment.1',
    /** Top-level `vct` of a Pryme-issued delegation SD-JWT. */
    prymeDelegation: 'id.pryme.ap2.checkout_delegation.1',
};
export const AP2_TYP = { issuer: 'dc+sd-jwt', terminal: 'kb+sd-jwt', intermediate: 'kb+sd-jwt+kb' };
export class Ap2Error extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.code = code;
        this.name = 'Ap2Error';
    }
}
const subtle = () => {
    const s = globalThis.crypto?.subtle;
    if (!s)
        throw new Error('WebCrypto (crypto.subtle) is required for AP2');
    return s;
};
const ECDSA = { name: 'ECDSA', namedCurve: 'P-256' };
const SIGN = { name: 'ECDSA', hash: 'SHA-256' };
/** Copy into a plain ArrayBuffer-backed view (WebCrypto's BufferSource typing). */
const buf = (u) => new Uint8Array(u);
const json = (v) => b64url.encode(utf8(JSON.stringify(v)));
const unjson = (s) => JSON.parse(new TextDecoder().decode(b64url.decode(s)));
export function publicJwk(k) {
    const out = { kty: 'EC', crv: 'P-256', x: k.x, y: k.y };
    if (k.kid)
        out.kid = k.kid;
    return out;
}
export function isEcPublicJwk(v) {
    const k = v;
    return !!k && k.kty === 'EC' && k.crv === 'P-256' && typeof k.x === 'string' && typeof k.y === 'string'
        && /^[A-Za-z0-9_-]{43}$/.test(k.x) && /^[A-Za-z0-9_-]{43}$/.test(k.y) && !('d' in k);
}
/** A fresh ES256 key pair (private JWK includes the public coordinates). */
export async function generateEs256Key(kid) {
    const pair = (await subtle().generateKey(ECDSA, true, ['sign', 'verify']));
    const j = (await subtle().exportKey('jwk', pair.privateKey));
    const out = { kty: 'EC', crv: 'P-256', x: j.x, y: j.y, d: j.d };
    if (kid)
        out.kid = kid;
    return out;
}
async function importKey(k, use) {
    const jwk = { kty: 'EC', crv: 'P-256', x: k.x, y: k.y, ext: true };
    if (use === 'sign')
        jwk.d = k.d;
    return subtle().importKey('jwk', jwk, ECDSA, false, [use]);
}
/** Compact JWS, ES256 (raw r||s signature, RFC 7518 §3.4). */
export async function signJwt(header, payload, key) {
    const input = `${json({ alg: 'ES256', ...header })}.${json(payload)}`;
    const sig = new Uint8Array(await subtle().sign(SIGN, await importKey(key, 'sign'), buf(utf8(input))));
    return `${input}.${b64url.encode(sig)}`;
}
export function decodeJwt(token) {
    const parts = token.split('.');
    if (parts.length !== 3)
        throw new Ap2Error('invalid_credential', 'malformed JWT');
    try {
        return { header: unjson(parts[0]), payload: unjson(parts[1]) };
    }
    catch {
        throw new Ap2Error('invalid_credential', 'malformed JWT encoding');
    }
}
/** Verify an ES256 compact JWS. Returns null on any failure (wrong alg, bad signature, malformed). */
export async function verifyJwt(token, key) {
    try {
        const parts = token.split('.');
        if (parts.length !== 3)
            return null;
        const d = decodeJwt(token);
        if (d.header.alg !== 'ES256' || d.header.crit !== undefined)
            return null;
        // One encoding per signature (security review L15): a variant in the unused trailing bits would
        // verify too and give the same mandate another `reference`. High-S stays accepted (Python's
        // `cryptography`, used by Google's reference SDK, produces it).
        if (!b64url.isCanonical(parts[2]))
            return null;
        const sig = b64url.decode(parts[2]);
        if (sig.length !== 64)
            return null;
        const ok = await subtle().verify(SIGN, await importKey(key, 'verify'), buf(sig), buf(utf8(`${parts[0]}.${parts[1]}`)));
        return ok ? d : null;
    }
    catch {
        return null;
    }
}
/** base64url(SHA-256(ascii(value))): AP2 `checkout_hash`, `sd_hash`, disclosure digests, receipt `reference`. */
export const ap2Hash = (value) => sha256b64(utf8(value));
/** RFC 9901 array-element disclosure `[salt, value]` and its digest. */
export function makeDisclosure(value) {
    const salt = b64url.encode(crypto.getRandomValues(new Uint8Array(16)));
    const disclosure = json([salt, value]);
    return { disclosure, digest: ap2Hash(disclosure) };
}
/** `<jwt>~<disclosure>~`: one SD-JWT whose `delegate_payload` holds one selectively disclosed item. */
async function sdJwtWithDelegate(header, claims, delegate, key) {
    const d = makeDisclosure(delegate);
    const jwt = await signJwt(header, { ...claims, _sd_alg: 'sha-256', delegate_payload: [{ '...': d.digest }] }, key);
    return `${jwt}~${d.disclosure}~`;
}
/** Issuer side (Pryme): sign a root SD-JWT carrying one open mandate (checkout or payment). */
export function issueOpenMandate(args) {
    if (!args.issuerKey.kid)
        throw new Error('issuer key needs a kid');
    return sdJwtWithDelegate({ typ: AP2_TYP.issuer, kid: args.issuerKey.kid }, { iss: args.issuer, vct: AP2_VCT.prymeDelegation, iat: args.mandate.iat, exp: args.mandate.exp, ...args.claims }, args.mandate, args.issuerKey);
}
/**
 * AP2 `open_checkout_hash`: the `sd_hash` of an open checkout mandate token (`<jwt>~<disclosures>~`).
 * An open payment mandate's `payment.reference` carries this value.
 */
export const openMandateHash = (token) => ap2Hash(token.endsWith('~') ? token : `${token}~`);
/** Terminal KB-SD-JWT hop over an open mandate; returns the chain `<open>~~<closed>~`. */
async function closeMandate(openMandate, holderKey, closed, aud, nonce) {
    if (!aud || !nonce)
        throw new Error('aud and nonce are required');
    const open = openMandate.endsWith('~') ? openMandate : `${openMandate}~`;
    const hop = await sdJwtWithDelegate({ typ: AP2_TYP.terminal }, { iat: Math.floor(Date.now() / 1000), aud, nonce, sd_hash: ap2Hash(open) }, closed, holderKey);
    return `${open.slice(0, -1)}~~${hop}`;
}
/**
 * Agent side: close an open checkout mandate with a terminal KB-SD-JWT hop signed by the `cnf` key.
 * Returns the AP2 dSD-JWT chain `<open>~~<closed>~` (draft-gco-oauth-delegate-sd-jwt-00). `aud`/`nonce` come from the merchant.
 */
export function presentCheckoutMandate(args) {
    const closed = { vct: AP2_VCT.checkout, checkout_jwt: args.checkoutJwt, checkout_hash: ap2Hash(args.checkoutJwt) };
    return closeMandate(args.openMandate, args.holderKey, closed, args.aud, args.nonce);
}
/**
 * Agent side: close an open payment mandate for the credential provider / payment processor.
 * `transaction_id` defaults to the hash of `checkoutJwt`, binding the payment to that checkout.
 */
export function presentPaymentMandate(args) {
    const closed = {
        vct: AP2_VCT.payment, transaction_id: ap2Hash(args.checkoutJwt), payee: args.payee, payment_amount: args.amount, payment_instrument: args.instrument,
    };
    return closeMandate(args.openMandate, args.holderKey, closed, args.aud, args.nonce);
}
function parseSdJwt(segment) {
    const s = segment.endsWith('~') ? segment : `${segment}~`;
    const parts = s.split('~');
    const jwt = parts[0];
    const disclosures = parts.slice(1, -1);
    if (jwt.split('.').length !== 3 || disclosures.some((d) => !d))
        throw new Ap2Error('invalid_credential', 'malformed SD-JWT');
    if (disclosures.some((d) => d.split('.').length === 3))
        throw new Ap2Error('invalid_credential', 'unexpected key-binding JWT in a delegation chain');
    return { jwt, disclosures, sdJwt: s };
}
/** Resolve RFC 9901 digests (`{"...": d}` array elements and `_sd` object members). Each disclosure is used at most once. */
function resolveDisclosures(payload, disclosures, alg) {
    if (alg !== undefined && alg !== 'sha-256')
        throw new Ap2Error('invalid_credential', `unsupported _sd_alg ${String(alg)}`);
    const byDigest = new Map();
    for (const d of disclosures) {
        let arr;
        try {
            arr = unjson(d);
        }
        catch {
            throw new Ap2Error('invalid_credential', 'malformed disclosure');
        }
        if (!Array.isArray(arr) || (arr.length !== 2 && arr.length !== 3))
            throw new Ap2Error('invalid_credential', 'malformed disclosure');
        const digest = ap2Hash(d);
        if (byDigest.has(digest))
            throw new Ap2Error('invalid_credential', 'duplicate disclosure');
        byDigest.set(digest, arr);
    }
    const used = new Set();
    const take = (digest) => {
        if (typeof digest !== 'string')
            throw new Ap2Error('invalid_credential', 'invalid digest');
        const arr = byDigest.get(digest);
        if (!arr)
            return undefined;
        if (used.has(digest))
            throw new Ap2Error('invalid_credential', 'digest referenced twice');
        used.add(digest);
        return arr;
    };
    const walk = (v) => {
        if (Array.isArray(v)) {
            const out = [];
            for (const el of v) {
                if (el && typeof el === 'object' && !Array.isArray(el) && Object.keys(el).length === 1 && '...' in el) {
                    const arr = take(el['...']);
                    if (!arr)
                        continue; // undisclosed element
                    if (arr.length !== 2)
                        throw new Ap2Error('invalid_credential', 'array disclosure must be [salt, value]');
                    out.push(walk(arr[1]));
                }
                else
                    out.push(walk(el));
            }
            return out;
        }
        if (v && typeof v === 'object') {
            const o = v;
            const out = {};
            for (const [k, val] of Object.entries(o))
                if (k !== '_sd' && k !== '_sd_alg')
                    out[k] = walk(val);
            for (const digest of Array.isArray(o._sd) ? o._sd : []) {
                const arr = take(digest);
                if (!arr)
                    continue;
                if (arr.length !== 3 || typeof arr[1] !== 'string')
                    throw new Ap2Error('invalid_credential', 'object disclosure must be [salt, name, value]');
                if (arr[1] in out || arr[1] === '_sd' || arr[1] === '...')
                    throw new Ap2Error('invalid_credential', `disclosed claim ${arr[1]} collides`);
                out[arr[1]] = walk(arr[2]);
            }
            return out;
        }
        return v;
    };
    const resolved = walk(payload);
    if (used.size !== byDigest.size)
        throw new Ap2Error('invalid_credential', 'disclosure not referenced by the signed payload');
    return resolved;
}
function singleDelegate(payload, label) {
    const dp = payload.delegate_payload;
    if (!Array.isArray(dp))
        return payload;
    const items = dp.filter((x) => x && typeof x === 'object' && !Array.isArray(x));
    if (items.length !== 1)
        throw new Ap2Error('invalid_credential', `${label}: delegate_payload must disclose exactly one item`);
    return items[0];
}
function checkTimes(p, now, skew, label) {
    if (p.exp !== undefined) {
        if (typeof p.exp !== 'number')
            throw new Ap2Error('invalid_credential', `${label}: invalid exp`);
        if (now > p.exp + skew)
            throw new Ap2Error('invalid_mandate', `${label}: expired`);
    }
    if (p.iat !== undefined) {
        if (typeof p.iat !== 'number')
            throw new Ap2Error('invalid_credential', `${label}: invalid iat`);
        if (p.iat > now + skew)
            throw new Ap2Error('invalid_credential', `${label}: iat in the future`);
    }
}
/**
 * Verify a two-hop AP2 checkout chain `<issuer SD-JWT>~~<KB-SD-JWT>~` (draft-gco-oauth-delegate-sd-jwt-00 §6):
 * issuer signature by `kid`, open mandate, terminal hop signed by the open mandate's `cnf`, `sd_hash`, `aud`, `nonce`, times.
 */
export async function verifyCheckoutChain(args) {
    const now = args.now ?? Math.floor(Date.now() / 1000);
    const skew = args.clockSkewSeconds ?? 60;
    const segments = args.presentation.split('~~');
    if (segments.length !== 2)
        throw new Ap2Error('invalid_credential', `checkout chain must have 2 tokens, got ${segments.length}`);
    const root = parseSdJwt(segments[0]);
    const hop = parseSdJwt(segments[1]);
    const rootHeader = decodeJwt(root.jwt).header;
    const key = args.issuerKeys.find((k) => k.kid && k.kid === rootHeader.kid);
    if (!key)
        throw new Ap2Error('invalid_credential', 'unknown issuer key id');
    const rv = await verifyJwt(root.jwt, key);
    if (!rv)
        throw new Ap2Error('invalid_credential', 'issuer signature invalid');
    const rootClaims = resolveDisclosures(rv.payload, root.disclosures, rv.payload._sd_alg);
    checkTimes(rootClaims, now, skew, 'issuer token');
    const open = singleDelegate(rootClaims, 'issuer token');
    if (open.vct !== AP2_VCT.openCheckout)
        throw new Ap2Error('mandates_not_supported', `expected ${AP2_VCT.openCheckout}, got ${String(open.vct)}`);
    checkTimes(open, now, skew, 'open mandate');
    if (!isEcPublicJwk(open.cnf?.jwk))
        throw new Ap2Error('invalid_credential', 'open mandate cnf.jwk must be an EC P-256 public key');
    if (!Array.isArray(open.constraints))
        throw new Ap2Error('invalid_mandate', 'open mandate has no constraints');
    const hopHeader = decodeJwt(hop.jwt).header;
    if (hopHeader.typ === AP2_TYP.intermediate)
        throw new Ap2Error('unresolved_constraint', 'checkout chain ends in an intermediate hop');
    if (hopHeader.typ !== AP2_TYP.terminal && hopHeader.typ !== 'kb-sd-jwt')
        throw new Ap2Error('invalid_credential', `unexpected hop typ ${String(hopHeader.typ)}`);
    const hv = await verifyJwt(hop.jwt, open.cnf.jwk);
    if (!hv)
        throw new Ap2Error('invalid_credential', 'closed mandate not signed by the key bound in the open mandate');
    const hopClaims = resolveDisclosures(hv.payload, hop.disclosures, hv.payload._sd_alg);
    const hasSd = 'sd_hash' in hopClaims;
    const hasIss = 'issuer_jwt_hash' in hopClaims;
    if (hasSd === hasIss)
        throw new Ap2Error('invalid_credential', 'closed hop needs exactly one of sd_hash / issuer_jwt_hash');
    if (hasSd ? hopClaims.sd_hash !== ap2Hash(root.sdJwt) : hopClaims.issuer_jwt_hash !== ap2Hash(root.jwt)) {
        throw new Ap2Error('invalid_credential', 'closed hop is not bound to this open mandate');
    }
    if (typeof hopClaims.iat !== 'number')
        throw new Ap2Error('invalid_credential', 'closed hop missing iat');
    if (hopClaims.aud !== args.expectedAud)
        throw new Ap2Error('invalid_credential', 'closed hop aud mismatch');
    if (hopClaims.nonce !== args.expectedNonce)
        throw new Ap2Error('invalid_credential', 'closed hop nonce mismatch');
    checkTimes(hopClaims, now, skew, 'closed hop');
    const closed = singleDelegate(hopClaims, 'closed hop');
    if ('cnf' in closed)
        throw new Ap2Error('invalid_credential', 'terminal hop must not carry cnf');
    if (closed.vct !== AP2_VCT.checkout)
        throw new Ap2Error('mandates_not_supported', `expected ${AP2_VCT.checkout}, got ${String(closed.vct)}`);
    checkTimes(closed, now, skew, 'closed mandate');
    if (typeof closed.checkout_jwt !== 'string' || typeof closed.checkout_hash !== 'string')
        throw new Ap2Error('invalid_mandate', 'closed mandate lacks checkout_jwt/checkout_hash');
    if (closed.checkout_hash !== ap2Hash(closed.checkout_jwt))
        throw new Ap2Error('invalid_mandate', 'checkout_hash does not match checkout_jwt');
    // AP2 rule 2: claims pre-set in the open mandate must be unchanged in the closed mandate.
    if (open.checkout_hash !== undefined && open.checkout_hash !== closed.checkout_hash) {
        throw new Ap2Error('invalid_mandate', 'closed mandate is for a different checkout than the issuer approved');
    }
    return { issuerClaims: rootClaims, issuerKid: String(rootHeader.kid), open, closed, reference: ap2Hash(hop.jwt) };
}
/**
 * Verify a two-hop AP2 payment chain: issuer signature, holder (cnf) signature, `sd_hash`, and the
 * closed payment against every constraint of the open one (an unknown constraint type is a violation).
 *
 * Safe by default (security review L14), for credential providers and processors: `expectedAud`,
 * `expectedNonce` and `expectedTransactionId` (the hash of the checkout JWT the payment is for) are
 * required, and `expectedOpenCheckoutHash`, when given, must equal the `payment.reference` constraint.
 *
 * `allowExpired` is evidence mode (Pryme tying processor evidence to its own reservation, after the
 * fact): the chain may have been presented to someone else, so audience, nonce and transaction are not
 * required; signatures and bindings are still checked, and the hop must have been made before the open
 * mandate expired.
 */
export async function verifyPaymentChain(args) {
    if (!args.allowExpired && (args.expectedAud === undefined || args.expectedNonce === undefined || args.expectedTransactionId === undefined)) {
        throw new Ap2Error('invalid_credential', 'expectedAud, expectedNonce and expectedTransactionId are required (allowExpired is for after-the-fact evidence only)');
    }
    const now = args.now ?? Math.floor(Date.now() / 1000);
    const skew = 60;
    const segments = args.presentation.split('~~');
    if (segments.length !== 2)
        throw new Ap2Error('invalid_credential', `payment chain must have 2 tokens, got ${segments.length}`);
    const root = parseSdJwt(segments[0]);
    const hop = parseSdJwt(segments[1]);
    const rootHeader = decodeJwt(root.jwt).header;
    const key = args.issuerKeys.find((k) => k.kid && k.kid === rootHeader.kid);
    if (!key)
        throw new Ap2Error('invalid_credential', 'unknown issuer key id');
    const rv = await verifyJwt(root.jwt, key);
    if (!rv)
        throw new Ap2Error('invalid_credential', 'issuer signature invalid');
    const rootClaims = resolveDisclosures(rv.payload, root.disclosures, rv.payload._sd_alg);
    const open = singleDelegate(rootClaims, 'issuer token');
    if (open.vct !== AP2_VCT.openPayment)
        throw new Ap2Error('mandates_not_supported', `expected ${AP2_VCT.openPayment}, got ${String(open.vct)}`);
    if (!isEcPublicJwk(open.cnf?.jwk))
        throw new Ap2Error('invalid_credential', 'open mandate cnf.jwk must be an EC P-256 public key');
    const hopHeader = decodeJwt(hop.jwt).header;
    if (hopHeader.typ !== AP2_TYP.terminal)
        throw new Ap2Error('invalid_credential', `unexpected hop typ ${String(hopHeader.typ)}`);
    const hv = await verifyJwt(hop.jwt, open.cnf.jwk);
    if (!hv)
        throw new Ap2Error('invalid_credential', 'closed mandate not signed by the key bound in the open mandate');
    const hopClaims = resolveDisclosures(hv.payload, hop.disclosures, hv.payload._sd_alg);
    if (hopClaims.sd_hash !== ap2Hash(root.sdJwt))
        throw new Ap2Error('invalid_credential', 'closed hop is not bound to this open mandate');
    if (typeof hopClaims.iat !== 'number')
        throw new Ap2Error('invalid_credential', 'closed hop missing iat');
    if (args.expectedAud !== undefined && hopClaims.aud !== args.expectedAud)
        throw new Ap2Error('invalid_credential', 'closed hop aud mismatch');
    if (args.expectedNonce !== undefined && hopClaims.nonce !== args.expectedNonce)
        throw new Ap2Error('invalid_credential', 'closed hop nonce mismatch');
    if (args.allowExpired) {
        // The hop itself must have been made while the open mandate was valid.
        const exp = typeof open.exp === 'number' ? open.exp : typeof rootClaims.exp === 'number' ? rootClaims.exp : undefined;
        if (exp !== undefined && hopClaims.iat > exp + skew)
            throw new Ap2Error('invalid_mandate', 'closed after the open mandate expired');
        if (hopClaims.iat > now + skew)
            throw new Ap2Error('invalid_credential', 'closed hop iat in the future');
    }
    else {
        checkTimes(rootClaims, now, skew, 'issuer token');
        checkTimes(open, now, skew, 'open mandate');
        checkTimes(hopClaims, now, skew, 'closed hop');
    }
    const closed = singleDelegate(hopClaims, 'closed hop');
    if (closed.vct !== AP2_VCT.payment)
        throw new Ap2Error('mandates_not_supported', `expected ${AP2_VCT.payment}, got ${String(closed.vct)}`);
    const amt = closed.payment_amount;
    if (!amt || !Number.isSafeInteger(amt.amount) || typeof amt.currency !== 'string')
        throw new Ap2Error('invalid_mandate', 'closed payment mandate lacks payment_amount');
    const violations = [];
    for (const c of open.constraints ?? []) {
        if (c.type === 'payment.amount_range') {
            if (c.currency !== amt.currency)
                violations.push(`currency ${amt.currency} != ${c.currency}`);
            if (amt.amount > c.max)
                violations.push(`amount ${amt.amount} exceeds maximum ${c.max}`);
            if (c.min !== undefined && amt.amount < c.min)
                violations.push(`amount ${amt.amount} below minimum ${c.min}`);
        }
        else if (c.type === 'payment.allowed_payees') {
            if (!c.allowed.some((m) => closed.payee && merchantMatches(m, closed.payee)))
                violations.push('payee not allowed');
        }
        else if (c.type === 'payment.reference') {
            const ref = c.conditional_transaction_id;
            if (args.expectedOpenCheckoutHash !== undefined && ref !== args.expectedOpenCheckoutHash)
                violations.push('payment.reference mismatch');
        }
        else {
            // A constraint this verifier does not understand may restrict acceptance: never ignore it (L14).
            violations.push(`Unknown constraint ${String(c.type)}`);
        }
    }
    if (args.expectedTransactionId !== undefined && closed.transaction_id !== args.expectedTransactionId)
        violations.push('transaction_id mismatch');
    if (open.payee && !(closed.payee && merchantMatches(open.payee, closed.payee)))
        violations.push('pre-set payee changed');
    if (open.payment_amount && (open.payment_amount.amount !== amt.amount || open.payment_amount.currency !== amt.currency))
        violations.push('pre-set amount changed');
    return { issuerClaims: rootClaims, issuerKid: String(rootHeader.kid), open, closed, reference: ap2Hash(hop.jwt), openHash: ap2Hash(root.sdJwt), violations };
}
/** Sign an AP2 Payment Receipt (payment processor side). It carries no amount: that comes from processor evidence. */
export function signPaymentReceipt(args) {
    const base = { iss: args.issuer, iat: Math.floor(Date.now() / 1000), reference: args.reference, payment_id: args.paymentId };
    const body = args.error
        ? { ...base, status: 'Error', error: args.error.code, error_description: args.error.description }
        : { ...base, status: 'Success', psp_confirmation_id: args.paymentId, network_confirmation_id: args.paymentId };
    return signJwt({ typ: 'JWT', kid: args.processorKey.kid }, body, args.processorKey);
}
/**
 * Same merchant? By id, and when both sides name a website, also by its host (security review L13):
 * a merchant id string alone is not proof of who the merchant is. Without ids: name and website.
 */
function merchantMatches(a, b) {
    const host = (w) => (URL.canParse(w) ? new URL(w).host.toLowerCase() : null);
    if (a.id && b.id)
        return a.id === b.id && (!a.website || !b.website || (host(a.website) !== null && host(a.website) === host(b.website)));
    return !!a.name && a.name === b.name && !!a.website && a.website === b.website;
}
/** Every cart unit must fit a requirement slot (capacity = quantity; empty acceptable_items = any item), as in AP2's reference. */
function lineItemsViolations(checkout, reqs) {
    const cart = new Map();
    for (const li of checkout.line_items ?? [])
        cart.set(li.item.id, (cart.get(li.item.id) ?? 0) + li.quantity);
    if (!cart.size)
        return ['Empty cart does not satisfy line_items constraint'];
    const skus = [...cart.keys()];
    const fits = (sku, j) => !reqs[j].acceptable_items?.length || reqs[j].acceptable_items.some((a) => a.id === sku);
    const stray = skus.filter((s) => !reqs.some((_, j) => fits(s, j)));
    if (stray.length)
        return stray.map((s) => `Item ${s} not in any requirement's acceptable items`);
    // Max flow: source → sku (cart qty) → requirement (∞) → sink (quantity).
    const n = 2 + skus.length + reqs.length;
    const cap = Array.from({ length: n }, () => new Array(n).fill(0));
    skus.forEach((s, i) => {
        cap[0][1 + i] = cart.get(s);
        reqs.forEach((_, j) => { if (fits(s, j))
            cap[1 + i][1 + skus.length + j] = Number.MAX_SAFE_INTEGER; });
    });
    reqs.forEach((r, j) => { cap[1 + skus.length + j][n - 1] = r.quantity; });
    let flow = 0;
    for (;;) {
        const prev = new Array(n).fill(-1);
        prev[0] = 0;
        const q = [0];
        while (q.length && prev[n - 1] === -1) {
            const u = q.shift();
            for (let v = 0; v < n; v++)
                if (prev[v] === -1 && cap[u][v] > 0) {
                    prev[v] = u;
                    q.push(v);
                }
        }
        if (prev[n - 1] === -1)
            break;
        let push = Number.MAX_SAFE_INTEGER;
        for (let v = n - 1; v !== 0; v = prev[v])
            push = Math.min(push, cap[prev[v]][v]);
        for (let v = n - 1; v !== 0; v = prev[v]) {
            cap[prev[v]][v] -= push;
            cap[v][prev[v]] += push;
        }
        flow += push;
    }
    const total = [...cart.values()].reduce((a, b) => a + b, 0);
    return flow < total ? ['Cannot satisfy line item constraints: cart has more units than the mandate allows'] : [];
}
/** Evaluate an open checkout mandate's constraints against a checkout. Unknown constraints fail (AP2). */
export function checkoutViolations(open, checkout) {
    const out = [];
    for (const c of open.constraints) {
        if (c.type === 'checkout.allowed_merchants') {
            if (!checkout.merchant)
                out.push('Missing merchant in checkout');
            else if (!c.allowed.some((a) => merchantMatches(a, checkout.merchant)))
                out.push(`Merchant ${checkout.merchant.name} not in allowed list`);
        }
        else if (c.type === 'checkout.line_items') {
            out.push(...lineItemsViolations(checkout, c.items));
        }
        else {
            out.push(`Unknown constraint ${c.type}`);
        }
    }
    return out;
}
/** Decode (not verify) a merchant checkout JWT's UCP Checkout payload. */
export function readCheckout(checkoutJwt) {
    const p = decodeJwt(checkoutJwt).payload;
    if (!p || typeof p.id !== 'string' || !Array.isArray(p.line_items) || !Array.isArray(p.totals) || typeof p.currency !== 'string') {
        throw new Ap2Error('invalid_mandate', 'checkout_jwt payload is not a UCP Checkout');
    }
    return p;
}
/** The checkout's `total` entry, in minor units. */
export function checkoutTotal(c) {
    const t = c.totals.filter((x) => x.type === 'total');
    if (t.length !== 1 || !Number.isSafeInteger(t[0].amount) || t[0].amount < 0)
        throw new Ap2Error('invalid_mandate', 'checkout must have exactly one non-negative integer total');
    return t[0].amount;
}
/**
 * Merchant side: verify an agent's AP2 checkout presentation against the checkout the merchant
 * itself issued (`checkoutJwt`, compared byte for byte) and the merchant's own identity.
 */
export async function verifyCheckoutPresentation(args) {
    try {
        const chain = await verifyCheckoutChain(args);
        if (chain.closed.checkout_jwt !== args.checkoutJwt)
            throw new Ap2Error('invalid_mandate', 'closed mandate carries a different checkout');
        const checkout = readCheckout(args.checkoutJwt);
        if (!checkout.merchant || !merchantMatches(checkout.merchant, args.merchant))
            throw new Ap2Error('invalid_mandate', 'checkout is not ours');
        const v = checkoutViolations(chain.open, checkout);
        if (v.length)
            throw new Ap2Error('invalid_mandate', v.join('; '));
        return { ok: true, ...chain, checkout };
    }
    catch (e) {
        if (e instanceof Ap2Error)
            return { ok: false, error: e.code, reason: e.message };
        return { ok: false, error: 'invalid_credential', reason: e.message };
    }
}
/** Merchant side: AP2 checkout receipt (Verifier-signed JWT, `reference` = hash of the closed-mandate JWT). */
export function signCheckoutReceipt(args) {
    const body = { iss: args.issuer, iat: Math.floor(Date.now() / 1000), reference: args.reference };
    if (args.error)
        Object.assign(body, { status: 'Error', error: args.error.code, error_description: args.error.description });
    else
        Object.assign(body, { status: 'Success', order_id: args.orderId });
    return signJwt({ typ: 'JWT', ...(args.merchantKey.kid ? { kid: args.merchantKey.kid } : {}) }, body, args.merchantKey);
}
